import { DependsOnThenEnum, IDependsOn, INestedDependsOn } from "@generation/schema-core";
import { DependsOnIfEnum } from "@generation/schema-core/src/client/enums/dependsOnIfEnum";
import { translate } from "@klantondersteuning-zvw/shared";
import { ISimpleForm, PredefinedFieldType, RegelingOptions } from "@klantondersteuning/shared";

const stopzetRedenen = {
  uitDienst: {
    value: "De uitkering stopt of de werknemer gaat uit dienst",
    label: translate("DeUitkeringStoptOfDeWerknemerGaatUitDienst")
  },
  uitkeringTeLaag: {
    value: "De uitkering of het salaris is te laag om zorgpremie in te houden",
    label: translate("DeUitkeringOfHetSalarisIsTeLaagOmPremieInTeHouden")
  },
  failliet: {
    value: "Het bedrijf is failliet",
    label: translate("HetBedrijfIsFailliet")
  },
  opgeheven: {
    value: "Het bedrijf is opgeheven",
    label: translate("HetBedrijfIsOpgeheven")
  },
  anders: {
    value: "Andere reden, namelijk...",
    label: translate("AndereRedenNamelijk")
  }
};

const dependsOnDeUitkeringStoptOfDeWerknemerGaatUitDienst: IDependsOn[] = [
  {
    property: "stap_mijnverzoek__aanleiding.redenStopzettenInhoudenPremie",
    if: DependsOnIfEnum.IsNotEqualToValue,
    value: stopzetRedenen.uitDienst.value,
    then: DependsOnThenEnum.Hide,
    isAbsolutePath: true
  }
];

const dependsOnDeUitkeringOfHetSalarisIsTeLaagOmPremieInTeHouden: IDependsOn[] = [
  {
    property: "stap_mijnverzoek__aanleiding.redenStopzettenInhoudenPremie",
    if: DependsOnIfEnum.IsNotEqualToValue,
    value: stopzetRedenen.uitkeringTeLaag.value,
    then: DependsOnThenEnum.Hide,
    isAbsolutePath: true
  }
];

const dependsOnHetBedrijfIsFailliet: IDependsOn[] = [
  {
    property: "stap_mijnverzoek__aanleiding.redenStopzettenInhoudenPremie",
    if: DependsOnIfEnum.IsNotEqualToValue,
    value: "Het bedrijf is failliet",
    then: DependsOnThenEnum.Hide,
    isAbsolutePath: true
  }
];

const dependsOnHetBedrijfIsOpgeheven: IDependsOn[] = [
  {
    property: "stap_mijnverzoek__aanleiding.redenStopzettenInhoudenPremie",
    if: DependsOnIfEnum.IsNotEqualToValue,
    value: stopzetRedenen.opgeheven.value,
    then: DependsOnThenEnum.Hide,
    isAbsolutePath: true
  }
];

const dependsOnHerinneringOfAanmaningJa: IDependsOn[] = [
  {
    property: "stap_mijnverzoek__aanleiding.herinneringOfAanmaning",
    if: DependsOnIfEnum.IsNotEqualToValue,
    value: "ja",
    then: DependsOnThenEnum.Hide,
    isAbsolutePath: true
  }
];

const dependsOnAndereRedenStopzettenInhoudenPremie: IDependsOn[] = [
  {
    property: "stap_mijnverzoek__aanleiding.redenStopzettenInhoudenPremie",
    if: DependsOnIfEnum.IsNotEqualToValue,
    value: stopzetRedenen.anders.value,
    then: DependsOnThenEnum.Hide,
    isAbsolutePath: true
  }
];

const nestedDependsOnFaillissementOfOpgeheven: INestedDependsOn[] = [
  {
    conditions: [
      {
        property: "stap_mijnverzoek__aanleiding.redenStopzettenInhoudenPremie",
        if: DependsOnIfEnum.IsNotEqualToValue,
        value: stopzetRedenen.failliet.value
      },
      {
        property: "stap_mijnverzoek__aanleiding.redenStopzettenInhoudenPremie",
        if: DependsOnIfEnum.IsNotEqualToValue,
        value: stopzetRedenen.opgeheven.value
      }
    ],
    then: DependsOnThenEnum.Hide,
    isAbsolutePath: true
  }
];

export const GetStopzettenBroninhoudingSchema = (): ISimpleForm => {
  return {
    name: "stopzetten-broninhouding-formulier",
    steps: [
      {
        name: translate("Inleiding"),
        isIntroduction: true
      },
      {
        name: translate("StopzettenBroninhoudingFormulierStapMijnVerzoek"),
        Sections: [
          {
            name: "stap_mijnverzoek__gegevens",
            fields: {
              voorletters: {
                predefinedFieldType: PredefinedFieldType.voorletters,
                isRequired: true
              },
              achternaam: {
                predefinedFieldType: PredefinedFieldType.achternaam,
                isRequired: true,
                autoComplete: true
              },
              bsn: {
                predefinedFieldType: PredefinedFieldType.bsn,
                isRequired: true
              },
              geboortedatum: {
                predefinedFieldType: PredefinedFieldType.geboortedatum,
                isRequired: true
              },
              regeling: {
                predefinedFieldType: PredefinedFieldType.regeling,
                isRequired: true,
                regelingOptions: [
                  RegelingOptions.BetalingsachterstandZorgpremie,
                  RegelingOptions.Onverzekerden
                ]
              }
            }
          },
          {
            name: "stap_mijnverzoek__aanleiding",
            fields: {
              herinneringOfAanmaning: {
                predefinedFieldType: PredefinedFieldType.radioYesNo,
                isRequired: true
              },
              redenStopzettenInhoudenPremie: {
                type: "select",
                ui: {
                  flexDirection: "column"
                },
                validate: {
                  isRequired: { value: true }
                },
                options: [
                  stopzetRedenen.uitDienst,
                  stopzetRedenen.uitkeringTeLaag,
                  stopzetRedenen.failliet,
                  stopzetRedenen.opgeheven,
                  stopzetRedenen.anders
                ]
              },
              werknemerStoptOfUitDienstBijlage: {
                predefinedFieldType: PredefinedFieldType.bijlage,
                isRequired: true,
                maxFiles: 1,
                dependsOn: [
                  ...dependsOnDeUitkeringStoptOfDeWerknemerGaatUitDienst,
                  ...dependsOnHerinneringOfAanmaningJa
                ]
              },
              uitkeringStoptOfUitDienstVanaf: {
                predefinedFieldType: PredefinedFieldType.datum,
                isRequired: true,
                dependsOn: [...dependsOnDeUitkeringStoptOfDeWerknemerGaatUitDienst]
              },
              uitkeringOfSalarisTeLaagBijlage: {
                predefinedFieldType: PredefinedFieldType.bijlage,
                isRequired: true,
                maxFiles: 1,
                dependsOn: [
                  ...dependsOnDeUitkeringOfHetSalarisIsTeLaagOmPremieInTeHouden,
                  ...dependsOnHerinneringOfAanmaningJa
                ]
              },
              maandEnJaar: {
                predefinedFieldType: PredefinedFieldType.textArea,
                isRequired: true,
                dependsOn: [...dependsOnDeUitkeringOfHetSalarisIsTeLaagOmPremieInTeHouden]
              },
              datumFaillissement: {
                predefinedFieldType: PredefinedFieldType.datum,
                isRequired: true,
                dependsOn: [...dependsOnHetBedrijfIsFailliet]
              },
              datumOpheffingBedrijf: {
                predefinedFieldType: PredefinedFieldType.datum,
                isRequired: true,
                dependsOn: [...dependsOnHetBedrijfIsOpgeheven]
              },
              aanvullendeInformatie: {
                predefinedFieldType: PredefinedFieldType.textArea,
                dependsOn: [...dependsOnHetBedrijfIsOpgeheven]
              },
              andereRedenStopzettenInhoudenPremie: {
                predefinedFieldType: PredefinedFieldType.textArea,
                isRequired: true,
                dependsOn: dependsOnAndereRedenStopzettenInhoudenPremie
              }
            }
          }
        ]
      },
      {
        name: translate("MijnGegevens"),
        Sections: [
          {
            name: "stap_mijngegevens__gegevens",
            fields: {
              naamBedrijf: {
                predefinedFieldType: PredefinedFieldType.text,
                isRequired: true
              },
              naamMedewerker: {
                predefinedFieldType: PredefinedFieldType.text
              },
              afdeling: {
                predefinedFieldType: PredefinedFieldType.text
              },
              kvkNummer: {
                type: "text",
                ui: {
                  flexDirection: "column"
                },
                validate: {
                  isRequired: { value: true },
                  kvkValidation: { value: true }
                },
                showIsValidCheckmark: true,
                nestedDependsOn: nestedDependsOnFaillissementOfOpgeheven
              },
              email: {
                predefinedFieldType: PredefinedFieldType.email,
                isRequired: true
              },
              telefoonnummer: {
                predefinedFieldType: PredefinedFieldType.telefoonnummer
              }
            }
          }
        ]
      }
    ]
  };
};
